import type { Legend } from '@/components/mapbox/legacy-types'
import {
	ActivePolygonControl,
	FilterConflictControl,
	LassoActionControl,
	LayerVisibilityControl,
	LegendControl,
	LoadingOverlayControl,
	StyleSwitcherControl
} from '@/utils/classes/MapboxCustomControls'
import type { FilterConflict } from '@accounts/types'
import type { Map } from 'mapbox-gl'
import { ref, watch } from 'vue'
import type { MapboxFeatureConfig } from './types'
import { DEFAULT_FEATURE_CONFIG } from './types'
import { useMapboxDrawing } from './useMapboxDrawing'
import { useMapboxState } from './useMapboxState'

export function useMapboxControls(
	mapId: string,
	config: MapboxFeatureConfig = DEFAULT_FEATURE_CONFIG
) {
	const { mapState, clearActivePolygon } = useMapboxState(mapId)
	const { clearLassoPolygon, exportGeoJson } = useMapboxDrawing(mapId, config)

	const mapControls = ref<string[]>([])
	const mapLayerVisibilityControl = ref<LayerVisibilityControl | null>(null)
	const mapActivePolygonControl = ref<ActivePolygonControl | null>(null)
	const mapLassoActionControl = ref<LassoActionControl | null>(null)
	const mapLoadingControl = ref<LoadingOverlayControl | null>(null)
	const mapFilterConflictControl = ref<FilterConflictControl | null>(null)
	const lastFilterConflicts = ref<FilterConflict[]>(mapState.value.filterConflicts ?? [])

	const resolveLoadingOverlayDebugFlag = () => {
		if (!import.meta.client || import.meta.env.VITE_RELEASE_STAGE !== 'development') {
			return false
		}
		const globalFlag = (window as typeof window & { __MX_DEBUG_MAPBOX_LOADING_OVERLAY__?: boolean }).__MX_DEBUG_MAPBOX_LOADING_OVERLAY__
		if (typeof globalFlag === 'boolean') {
			return globalFlag
		}
		try {
			const params = new URLSearchParams(window.location.search)
			const paramValue = params.get('debugLoadingOverlay')
			if (paramValue !== null) {
				return paramValue === 'true'
			}
		} catch (error) {
			console.warn('[Mapbox] Failed to parse debugLoadingOverlay query param', error)
		}
		try {
			return window.localStorage?.getItem('mx-debug-loading-overlay') === 'true'
		} catch (error) {
			console.warn('[Mapbox] Unable to access localStorage for loading overlay debug flag', error)
		}
		return false
	}

	// Register a custom control position
	const registerControlPosition = (map: Map, positionName: string) => {
		if (map._controlPositions[positionName as keyof typeof map._controlPositions]) {
			return
		}
		const positionContainer = document.createElement('div')
		positionContainer.className = `mapboxgl-ctrl-${positionName}`
		map._controlContainer.appendChild(positionContainer)
		map._controlPositions[positionName as keyof typeof map._controlPositions] = positionContainer
	}

	// Add a control if it doesn't already exist
	const addControlIfNotExists = (
		map: Map,
		control: mapboxgl.IControl,
		controlName: string,
		controlPosition: string
	) => {
		if (!mapControls.value.includes(controlName)) {
			map.addControl(control, controlPosition as mapboxgl.ControlPosition)
			mapControls.value.push(controlName)
		}
	}

	// Initialize style switcher control
	const initStyleSwitcherControl = (map: Map, styles: { label: string
		url: string }[]) => {
		const styleSwitcherOptions = new StyleSwitcherControl(styles)
		map.addControl(styleSwitcherOptions, 'top-left')
	}

	// Initialize layer visibility control
	const initLayerVisibilityControl = (
		map: Map,
		layers: { name: string
			id: string
			visibility: boolean
			hideInVisibilityControl: boolean
			siblingLayerIds: string[] }[]
	) => {
		mapLayerVisibilityControl.value = new LayerVisibilityControl(map, layers)
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		addControlIfNotExists(map, mapLayerVisibilityControl.value, 'layer-visibility-control', 'top-right')
	}

	// Initialize active polygon control
	const initActivePolygonControl = (map: Map, label: string) => {
		mapActivePolygonControl.value = new ActivePolygonControl(label, clearActivePolygon)
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		addControlIfNotExists(map, mapActivePolygonControl.value, 'active-polygon-control', 'top-center')
	}

	// Initialize legend control
	const initLegendControl = (map: Map, legends: Legend[]) => {
		const legendControl = new LegendControl(legends, map)
		map.addControl(legendControl, 'top-left')
	}

	const initLoadingControl = (map: Map) => {
		if (mapLoadingControl.value) {
			return mapLoadingControl.value
		}
		const keepVisibleOnLoad = resolveLoadingOverlayDebugFlag()
		mapLoadingControl.value = new LoadingOverlayControl({ debugKeepVisible: keepVisibleOnLoad })
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore - the placeholder element is hidden but control registration is required
		map.addControl(mapLoadingControl.value, 'top-left')
		if (!mapControls.value.includes('loading-overlay-control')) {
			mapControls.value.push('loading-overlay-control')
		}
		devOnly(() => {
			const globalWindow = window as typeof window & {
				mxMapboxLoadingOverlayDebug?: {
					enable: () => void
					disable: () => void
					toggle: () => void
					set: (value: boolean) => void
					isEnabled: () => boolean
				}
			}
			globalWindow.mxMapboxLoadingOverlayDebug = {
				enable: () => {
					try {
						window.localStorage?.setItem('mx-debug-loading-overlay', 'true')
					} catch (error) {
						console.warn('[Mapbox] Unable to persist loading overlay debug flag', error)
					}
					mapLoadingControl.value?.setDebugKeepVisible(true)
				},
				disable: () => {
					try {
						window.localStorage?.setItem('mx-debug-loading-overlay', 'false')
					} catch (error) {
						console.warn('[Mapbox] Unable to persist loading overlay debug flag', error)
					}
					mapLoadingControl.value?.setDebugKeepVisible(false)
				},
				toggle: () => {
					const nextValue = !(mapLoadingControl.value?.isDebugKeepVisible() ?? false)
					try {
						window.localStorage?.setItem('mx-debug-loading-overlay', nextValue ? 'true' : 'false')
					} catch (error) {
						console.warn('[Mapbox] Unable to persist loading overlay debug flag', error)
					}
					mapLoadingControl.value?.setDebugKeepVisible(nextValue)
				},
				set: (value: boolean) => {
					try {
						window.localStorage?.setItem('mx-debug-loading-overlay', value ? 'true' : 'false')
					} catch (error) {
						console.warn('[Mapbox] Unable to persist loading overlay debug flag', error)
					}
					mapLoadingControl.value?.setDebugKeepVisible(value)
				},
				isEnabled: () => mapLoadingControl.value?.isDebugKeepVisible() ?? false
			}
		})
		return mapLoadingControl.value
	}

	const showLoadingOverlay = () => {
		mapLoadingControl.value?.show()
	}

	const hideLoadingOverlay = () => {
		mapLoadingControl.value?.hide()
	}

	const ensureFilterConflictControlPriority = () => {
		const controlContainer = mapFilterConflictControl.value?.getContainer?.()
		const positionContainer = controlContainer?.parentElement
		if (!controlContainer || !positionContainer) {
			return
		}
		if (positionContainer.firstElementChild !== controlContainer) {
			positionContainer.prepend(controlContainer)
		}
	}

	const updateFilterConflictControl = (conflicts: FilterConflict[]) => {
		lastFilterConflicts.value = conflicts
		if (!mapFilterConflictControl.value) {
			return
		}
		mapFilterConflictControl.value.updateConflicts(conflicts)
		ensureFilterConflictControlPriority()
	}

	const initFilterConflictControl = (map: Map) => {
		if (mapFilterConflictControl.value) {
			return mapFilterConflictControl.value
		}
		mapFilterConflictControl.value = new FilterConflictControl(lastFilterConflicts.value)
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		addControlIfNotExists(map, mapFilterConflictControl.value, 'filter-conflict-control', 'top-center')
		ensureFilterConflictControlPriority()
		return mapFilterConflictControl.value
	}

	// Initialize lasso action control
	const initLassoActionControl = (map: Map, hasExportPermission: boolean) => {
		const getLassoPolygonIds = () => mapState.value.lassoFeatures.map(
			feature => feature.properties?.[config.idProperty]
		).filter(id => id) as string[]

		mapLassoActionControl.value = new LassoActionControl(
			getLassoPolygonIds(),
			clearLassoPolygon,
			exportGeoJson,
			hasExportPermission
		)
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		addControlIfNotExists(map, mapLassoActionControl.value, 'lasso-polygon-control', 'top-center')
	}

	// Update lasso action control
	const updateLassoActionControl = () => {
		if (!mapLassoActionControl.value) {
			return
		}

		const lassoIds = mapState.value.lassoFeatures
			.map(feature => feature.properties?.[config.idProperty])
			.filter(id => id) as string[]

		mapLassoActionControl.value.updateLabel(lassoIds)
	}

	// Update active polygon control
	const updateActivePolygonControl = (label: string) => {
		if (!mapActivePolygonControl.value) {
			return
		}
		mapActivePolygonControl.value.updateLabel(label)
	}

	// Watch for lasso features changes to update the control
	watch(() => mapState.value.lassoFeatures, () => {
		updateLassoActionControl()
	}, { deep: true })

	// Watch for changes to activePolygonLabel prop and from state then update the control
	watch(() => mapState.value.activePolygonLabel, stateLabel => {
		const newLabel = stateLabel || ''
		updateActivePolygonControl(newLabel)
	})

	watch(() => mapState.value.filterConflicts, conflicts => {
		updateFilterConflictControl(conflicts ?? [])
	}, {
		deep: true,
		immediate: true
	})

	return {
		registerControlPosition,
		addControlIfNotExists,
		initStyleSwitcherControl,
		initLayerVisibilityControl,
		initActivePolygonControl,
		initLegendControl,
		initLassoActionControl,
		initLoadingControl,
		initFilterConflictControl,
		showLoadingOverlay,
		hideLoadingOverlay,
		updateActivePolygonControl,
		updateFilterConflictControl
	}
}
